<template>
    <BasePicker ref="basePicker" v-bind="{...basePickerProps}">
        <template #default="{datePickerPos}">
            <vue-date-picker
                ref="picker"
                inline
                locale="nb"
                auto-apply
                month-picker
                arrow-navigation
                :format-locale="nb"
                :min-date="SYS_MIN_DATE"
                :enable-time-picker="false"
                :model-value="monthModel"
                :config="{onClickOutside: (e: any) => handleOnSubmit(e)}"
                :style="datePickerPos"
                @update:model-value="onInternalPickerUpdate"
            />
        </template>
    </BasePicker>
</template>

<script setup lang="ts">
import {computed, nextTick, PropType, ref, useTemplateRef} from 'vue'
import {nb} from 'date-fns/locale'
import VueDatePicker from '@vuepic/vue-datepicker'
import {formattedDate} from '@/services/formatter.service'
import {ToastType, usePanelToast} from '@tritt/multipanel'
import {parseString, SYS_MIN_DATE} from '@/dateUtils/dateUtils'
import {LINE_COLUMN} from '@/transaction/create-transaction/EntryLineInstance'
import BasePicker from '@/dateUtils/BasePicker.vue'
import {addMonths, subMonths} from 'date-fns'
import emitter from '@/services/infrastructure/emitter'
import {FOCUS_TRANS_SUBMIT} from '@/types/events'
import {HTMLInputElement} from 'happy-dom'

type MonthPickerType = { month: number | string, year: number | string }
const {showPanelToast} = usePanelToast()
const picker = ref()

const props = defineProps({
    dateId: String,
    size: {
        type: String,
        default: 'md'
    },
    label: String,
    hideCalenderIcon: {
        type: Boolean,
        default: false
    },
    isError: {
        type: Boolean,
        default: false
    },
    isDisabled: Boolean,
    dateFormat: {
        type: String,
        default: undefined
    },
    dataColId: {
        type: String as PropType<LINE_COLUMN>,
        default: undefined
    },
    yearColorHint: {
        type: Boolean,
        default: false
    },
    disableBackgroundColorClass: {
        type: Boolean,
        default: true
    }
})

const basePickerProps = computed(() => ({
    ...props,
    year: year.value,
    dateStr: dateStr.value,
    pageUp,
    pageDown,
    handleOnSubmit,
    focusPicker
}))

const monthModel = defineModel<MonthPickerType>()

const currentDate = computed(() => {
    let month = monthModel?.value?.month
    let year = monthModel?.value?.year

    if ((!month && month !== 0) || !year) return

    month = typeof month === 'number' ? month : parseInt(month)
    year = typeof year === 'number' ? year : parseInt(year)

    return new Date(year, month)
})

const dateStr = computed(() => {
    if (!currentDate.value) return

    const formatter = new Intl.DateTimeFormat('no', {month: 'short', year: '2-digit'})
    return formatter.format(currentDate.value).replace(/^./, (char) => char.toUpperCase())
})
const year = computed(() => monthModel.value?.year?.toString())

const basePicker = useTemplateRef<InstanceType<typeof BasePicker>>('basePicker')

const pageUp = () => {
    if (!currentDate.value) return

    const newDate = subMonths(currentDate.value, 1)
    monthModel.value = {month: newDate.getMonth(), year: newDate.getFullYear()}
}

const pageDown = () => {
    if (!currentDate.value) return

    const newDate = addMonths(currentDate.value, 1)
    monthModel.value = {month: newDate.getMonth(), year: newDate.getFullYear()}
}

const handleOnSubmit = (event: KeyboardEvent) => {
    const inputStr = (event?.target as unknown as HTMLInputElement)?.value
    if (!inputStr) {
        basePicker?.value?.hide()
        return
    }

    const parsedDate = !monthModel.value?.month && !monthModel.value?.year
        ? parseString(inputStr, new Date())
        : parseString(
            inputStr,
            new Date(
                typeof monthModel.value.year === 'number'
                    ? monthModel.value.year
                    : parseInt(monthModel.value.year),
                typeof monthModel.value.month === 'number'
                    ? monthModel.value.month
                    : parseInt(monthModel.value.month)
            )
        )

    if (!parsedDate) return

    const isValidDate = parsedDate >= SYS_MIN_DATE

    if (!isValidDate) showPanelToast('Forfallsdato endret til: ' + formattedDate(SYS_MIN_DATE), undefined, ToastType.WARNING)

    monthModel.value = isValidDate
        ? {
            month: parsedDate.getMonth(),
            year: parsedDate.getFullYear()
        }
        : {
            month: SYS_MIN_DATE.getMonth(),
            year: SYS_MIN_DATE.getFullYear()
        }

    basePicker?.value?.hide()
}

const onInternalPickerUpdate = (value: any) => {
    monthModel.value = value

    nextTick(() => {
        basePicker?.value?.hide()
        emitter.emit(FOCUS_TRANS_SUBMIT)
    })
}

const focusBasePicker = () => basePicker?.value?.focus()
const focusPicker = () => picker?.value?.$el.querySelectorAll('.dp__outer_menu_wrap .dp__menu')[0].focus()

defineExpose({
    focus: focusBasePicker,
})

</script>

<style>
</style>